import { createElement, useCallback, useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import Button from '../ui/Button';

// The tier of a page that is not on the Console (DESIGN rev 2.1 §3.3): non-review Portal pages are light by default, review
// pages dark, each with the toggle. The viewer's choice is kept in localStorage under `key` (per user when signed in), and
// the tier is set on <html> while the page is open (dialogs and toasts render at the app root), then put back on leaving.
// The staff Console preference (hf-theme) is never touched.
//   usePageTier({ key, fallback }) → { tier, toggle }
export function usePageTier({ key, fallback = 'light' }) {
  const read = () => { try { const v = localStorage.getItem(key); return v === 'light' || v === 'dark' ? v : fallback; } catch { return fallback; } };
  const [tier, setTier] = useState(read);
  const toggle = useCallback(() => setTier(t => {
    const next = t === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(key, next); } catch { /* private mode: the choice lasts for this page only */ }
    return next;
  }), [key]);
  // Runs after the shell's own theme effect (a parent's effects follow its children's), so the page tier wins.
  useEffect(() => {
    const html = document.documentElement;
    const before = html.getAttribute('data-theme');
    return () => { if (before) html.setAttribute('data-theme', before); else html.removeAttribute('data-theme'); };
  }, []);
  useEffect(() => { document.documentElement.setAttribute('data-theme', tier); }, [tier]);
  return { tier, toggle };
}

// Non-review Portal pages (talent pages, client Storage): light by default, kept per user under hf-portal-theme (the auth
// pages use the same key without a user).
export const usePortalTier = user => usePageTier({ key: user ? `hf-portal-theme:${user.id}` : 'hf-portal-theme', fallback: 'light' });

// The tier control for a Portal page: an icon button, 44 px on Portal pages.
export function PortalTierToggle({ tier, toggle, portal = true }) {
  return createElement(Button, {
    variant: 'quiet', iconOnly: true, portal, icon: tier === 'dark' ? Sun : Moon,
    label: tier === 'dark' ? 'Light theme' : 'Dark theme', onClick: toggle,
  });
}
